import Big from 'big.js';
import { BillingRate, Facility, OrientationModel } from '../../models';
import { FormulaHelperService } from '../../services/formula-helper.service';
import {
    PayBillingActions,
    MarginCalculatorPageActions as PageActions,
    MarginCalculatorComputationActions,
    MarginCalculatorApiActions
} from '../actions';
import { NumberHelperService } from '../../services/number-helper.service';
import { MarginCalculatorPayBillingHelperService as helper } from '../../services/margin-calculator-pay-billing-helper.service';
import { ControlDefaults } from '../../enum/control-defaults.enum';
import { HoursFrequency } from '../../services/margin-calculator-pay-billing.service';

export class PayBillingStateHelpers {
    /**
     * Returns a recalculated meal and housing stipend anytime someone provides an overall stipend amount and not using aya housing
     *
     * @param weeklyStipend an entered stipend amount that covers both housing and meals
     * @param maxMealStipend the max meals stipend from the gsa limits
     * @param maxHousingStipend the max housing stipend from the gsa limits
     * @returns { mealStipend, housingStipend } which are the new calculated meal and housing stipend amounts
     */
    static calculateMealsAndHousingStipendAmountsFromWeeklyStipend(
        weeklyStipend: number,
        maxMealStipend: number,
        maxHousingStipend: number
    ): { mealStipend: number; housingStipend: number } {
        const weeklyStipendFortyPercent = FormulaHelperService.roundNumber(weeklyStipend * 0.4);
        const weeklyStipendSixtyPercent = FormulaHelperService.roundNumber(weeklyStipend * 0.6);
        let weeklyMealsStipend = 0;
        let weeklyHousingStipend = 0;

        if (weeklyStipendFortyPercent > maxMealStipend) {
            weeklyMealsStipend = maxMealStipend;
            weeklyHousingStipend = weeklyStipend - maxMealStipend;
        } else if (
            weeklyStipend <= maxMealStipend + maxHousingStipend &&
            weeklyStipendSixtyPercent > maxHousingStipend
        ) {
            weeklyHousingStipend = maxHousingStipend;
            weeklyMealsStipend = weeklyStipend - maxHousingStipend;
        } else {
            weeklyMealsStipend = weeklyStipendFortyPercent;
            weeklyHousingStipend = weeklyStipendSixtyPercent;
        }

        return { mealStipend: weeklyMealsStipend, housingStipend: weeklyHousingStipend };
    }

    static getStipendsPerDay(stipendPerWeek: number): number {
        return new Big(stipendPerWeek).div(7).round(2, Big.roundHalfUp).toNumber();
    }

    static getStipendsPerShift(stipendsPerDay: number, shifts: number): number {
        if (shifts === 0) {
            return 0;
        }
        return new Big(stipendsPerDay).times(7).div(shifts).round(2, Big.roundHalfUp).toNumber();
    }

    static getTotalStipends(mealStipend: number, housingStipend: number): number {
        return new Big(mealStipend).add(housingStipend).round(2, Big.roundHalfUp).toNumber();
    }

    static getWeeklyStipends(stipendPerWeek: number): number {
        const dailyStipends = PayBillingStateHelpers.getStipendsPerDay(stipendPerWeek);
        return new Big(dailyStipends).times(7).round(2, Big.roundHalfUp).toNumber();
    }

    /**
     * Returns the gsa link for the provided zip
     *
     * @param startDate start date from the margin
     * @param facility facility from the margin
     * @returns string that contains the gsa link
     */
    static buildGSALink(startDate: Date, facility: Facility): string {
        if (facility?.hospitalZip) {
            let year = startDate.getFullYear();
            //The fiscal year runs from October 1st to September 30th
            if (startDate.getMonth() > 8) {
                //January is 0 ... September is 8
                year = startDate.getFullYear() + 1;
            }
            return (
                `https://www.gsa.gov/travel/plan-book/per-diem-rates/per-diem-rates-results/` +
                `?action=perdiems_report&fiscal_year=${year}&city=&state=&zip=${facility.hospitalZip}`
            );
        }

        return '';
    }

    static calculateAdjustedBillRate(
        extension: boolean,
        billRate: number,
        paybackPercent: number,
        extDiscount: number,
        discountPercentDecimal: number,
        extDiscountType: boolean
    ): number {
        let adjustedBillRate = billRate * (1 - paybackPercent) * (1 - discountPercentDecimal);

        if (extension) {
            if (extDiscountType) {
                adjustedBillRate -= extDiscount * 100;
            } else {
                adjustedBillRate *= 1 - extDiscount;
            }
        }

        return FormulaHelperService.roundNumber(adjustedBillRate);
    }

    static calculateAdjustedPremiumRate(facilityState: string, basePayRate: number, premiumRate: number): number {
        if (facilityState === 'AK' || facilityState === 'CA' || facilityState === 'VI') {
            const adjustedPremiumRate = FormulaHelperService.calculateMinPremiumPayRate(basePayRate);
            if (premiumRate > adjustedPremiumRate) {
                return adjustedPremiumRate;
            }
        }

        return premiumRate;
    }

    static calculateTotalOrientation(
        paybackPct: number,
        extDiscount: number,
        discountPercent: number,
        extension: boolean,
        facility: Facility,
        orientationParams: OrientationModel,
        adjustedBillRate: number
    ): number {
        let billTotal = 0;

        if (facility && (paybackPct > -1 || discountPercent > -1 || extDiscount > -1)) {
            let percentOfHours = 0;
            let percentOfHoursAlt = 0;
            let rate = 0;
            let rateAlt = 0;
            const orientHours = orientationParams.orientHours;
            const orientHoursAlt = orientationParams.orientHoursAlt;
            let orientRate = NumberHelperService.formatNumber(orientationParams.orientRate);
            let orientRateAlt = NumberHelperService.formatNumber(orientationParams.orientRateAlt);

            const contractFee = paybackPct;
            const extFee = extDiscount;
            const earlyPmtDiscount = discountPercent;

            if (
                orientationParams.orientType === ControlDefaults.OrientationPercent &&
                (orientationParams.allHoursBillable || orientationParams.orientRate > 0)
            ) {
                orientRate = FormulaHelperService.adjustOrientRate(orientRate);
                const orientationRate = FormulaHelperService.roundNumber(
                    new Big(NumberHelperService.formatNumber(adjustedBillRate)).times(orientRate).toNumber()
                );
                percentOfHours = Big(orientHours).times(orientationRate).toNumber();
            }

            if (
                orientationParams.orientTypeAlt === ControlDefaults.OrientationPercent &&
                (orientationParams.allHoursBillable || orientationParams.orientRateAlt > 0)
            ) {
                orientRateAlt = FormulaHelperService.adjustOrientRate(orientRateAlt);
                const orientationRate = FormulaHelperService.roundNumber(
                    new Big(NumberHelperService.formatNumber(adjustedBillRate)).times(orientRateAlt).toNumber()
                );
                percentOfHoursAlt = Big(orientHoursAlt).times(orientationRate).toNumber();
            }

            if (
                orientationParams.orientType === ControlDefaults.OrientationDollar &&
                (orientationParams.allHoursBillable || orientationParams.orientRate > 0)
            ) {
                rate = FormulaHelperService.calculateAdjustedOrientationRate(
                    extension,
                    orientHours,
                    orientationParams.orientRate,
                    contractFee,
                    extFee,
                    earlyPmtDiscount,
                    facility.extDiscountType
                );
            }

            if (
                orientationParams.orientTypeAlt === ControlDefaults.OrientationDollar &&
                (orientationParams.allHoursBillable || orientationParams.orientRateAlt > 0)
            ) {
                rateAlt = FormulaHelperService.calculateAdjustedOrientationRate(
                    extension,
                    orientHoursAlt,
                    orientationParams.orientRateAlt,
                    contractFee,
                    extFee,
                    earlyPmtDiscount,
                    facility.extDiscountType
                );
            }

            billTotal = Big(Big(percentOfHours).plus(percentOfHoursAlt).plus(rate).plus(rateAlt))
                .times(100)
                .round()
                .div(100)
                .toNumber();
        }

        return billTotal;
    }

    static actionTriggersBillingDetailsReset(action: string): boolean {
        const list = [
            PayBillingActions.setMarginPayRate.type.toString(),
            PageActions.onMarginPayRateChanged.type,
            PageActions.baseBillingRateChanged.type,
            PageActions.setBaseBillingRate.type,
            PageActions.specialtyIdSelected.type,
            PageActions.setShiftTime.type,
            PageActions.setShiftHours.type,
            PageActions.facilitySelected.type,
            MarginCalculatorApiActions.billingRatesLoadedSuccess.type,
            MarginCalculatorComputationActions.baseBillingRateComputedSuccess.type
        ];

        return list.includes(action);
    }

    static getOvertimeHoursRule(count: number, shiftHours: number, billingRates: BillingRate[]): number {
        if (billingRates && billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                billingRates[0][`overTimeMultAmount${1}${shiftHours}`] as string,
                billingRates[0][`overTimeHours${1}${shiftHours}`] as number,
                billingRates[0][`overTimeFreq${1}${shiftHours}`] as string
            );

            let overtimeHours = billingRates?.[0] ? (billingRates[0][`overTimeHours${count}A`] as number) : 0;

            if (shiftOvertimeRuleExists) {
                overtimeHours = billingRates?.[0]
                    ? (billingRates[0][`overTimeHours${count}${shiftHours}`] as number)
                    : 0;
            }
            return overtimeHours;
        }

        return 0;
    }

    static getOvertimeHoursFrequency(count: number, shiftHours: number, billingRates: BillingRate[]): string {
        if (billingRates && billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                billingRates[0][`overTimeMultAmount${1}${shiftHours}`] as string,
                billingRates[0][`overTimeHours${1}${shiftHours}`] as number,
                billingRates[0][`overTimeFreq${1}${shiftHours}`] as string
            );

            let overtimeFreq = billingRates?.[0]
                ? (billingRates[0][`overTimeFreq${count}A`] as string)
                : ControlDefaults.EmptySelect;

            if (shiftOvertimeRuleExists) {
                overtimeFreq = billingRates?.[0]
                    ? billingRates[0][`overTimeFreq${count}${shiftHours}`] &&
                      billingRates[0][`overTimeFreq${count}${shiftHours}`] !== ''
                        ? (billingRates[0][`overTimeFreq${count}${shiftHours}`] as string)
                        : ControlDefaults.EmptySelect
                    : ControlDefaults.EmptySelect;
            }
            return overtimeFreq;
        }

        return ControlDefaults.EmptySelect;
    }

    static getOvertimeAndOrRule(shiftHours: number, billingRates: BillingRate[]): string {
        if (billingRates && billingRates.length > 0) {
            const shiftOvertimeRuleExists = helper.shiftOvertimeRuleExists(
                billingRates[0][`overTimeMultAmount${1}${shiftHours}`] as string,
                billingRates[0][`overTimeHours${1}${shiftHours}`] as number,
                billingRates[0][`overTimeFreq${1}${shiftHours}`] as string
            );

            let overtimeAndOr = billingRates?.[0] ? billingRates[0].overTimeAndOrA : '';

            const andOr = billingRates?.[0] ? (billingRates[0][`overTimeAndOr${shiftHours}`] as string) : '';

            if (shiftOvertimeRuleExists) {
                overtimeAndOr = andOr && andOr !== '' ? andOr : ControlDefaults.EmptySelect;
            }
            return overtimeAndOr;
        }

        return ControlDefaults.EmptySelect;
    }

    static getDisableFailSafe(billingRate?: BillingRate): boolean {
        return billingRate?.disableOtFailSafe ?? false;
    }

    static shouldEnableOvertimeBillingRule(
        isAlternatingShift: boolean,
        shifts: number,
        altShifts: number,
        shiftHours: number,
        altShiftHours: number,
        otFirstFrequency: string,
        otSecondFrequency: string,
        otAndOr: string,
        otFirstHours: number,
        billingRate?: BillingRate
    ): boolean {
        let enableOvertimeBillingRule = true;
        const amt = shifts * shiftHours;
        const altAmt = isAlternatingShift ? altShifts * altShiftHours : 0;

        if (
            otFirstFrequency === HoursFrequency.DAILY.toString() ||
            otSecondFrequency === HoursFrequency.DAILY.toString()
        ) {
            enableOvertimeBillingRule = true;
        } else if (amt > otFirstHours || altAmt > otFirstHours) {
            if (
                otFirstFrequency !== HoursFrequency.DAILY.toString() &&
                (otSecondFrequency !== HoursFrequency.DAILY.toString() ||
                    otAndOr === ControlDefaults.EmptySelect.toString()) &&
                !PayBillingStateHelpers.getDisableFailSafe(billingRate)
            ) {
                enableOvertimeBillingRule = false;
            }
        }

        return enableOvertimeBillingRule;
    }

    static calculatePremiumMargin(premiumBillRate: number, otWage: number, multiplier?: number): number {
        return premiumBillRate > 0
            ? new Big(premiumBillRate)
                  .minus(new Big(otWage).times(multiplier + 1))
                  .div(premiumBillRate)
                  .toNumber()
            : 0;
    }

    static hasMultipleRules(type: string, shiftHours: number, billingRates: BillingRate[]): boolean {
        const andOr = billingRates?.[0] ? billingRates[0][`${type}AndOr${shiftHours}`] : null;

        if (shiftHours && andOr) {
            return billingRates[0][`${type}AndOr${shiftHours}`] !== '';
        }
        return !andOr && billingRates?.[0] ? billingRates[0][`${type}AndOrA`] !== '' : false;
    }
}
